import { type EditorState } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';
import { type Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { type ReactNode } from 'react';
import { getSelectionStartAnchor } from '../core';
import { getDialogContainer } from './get-dialog-container';
import { Toolbar } from './toolbar-namespace';

export interface TextMenuWrapperProps {
  editor: Editor;
  shouldShow: (props: { view: EditorView; state: EditorState; from: number }) => boolean;
  /**
   * Host-controlled visibility gate (design D5's "toolbar visibility"
   * concrete option), folded into `shouldShow`. The app wrapper at the legacy
   * path wires this to its `useIsMobile()` — a debounced-resize breakpoint
   * with documented quirks that deliberately stays app infrastructure (the
   * coding package avoided duplicating it for the same reasons). Undefined
   * means never suppressed.
   */
  suppressed?: boolean;
  children: ReactNode;
}

/**
 * The bubble-menu frame every text menu renders through (6.5/6.6 wave B-1):
 * selection-anchored, dialog-aware placement over the host editor, with the
 * shared toolbar chrome. Moved verbatim from the app's `text-menu-base` except
 * for the `suppressed` inversion documented above.
 */
export const TextMenuWrapper = ({
  editor,
  shouldShow,
  suppressed,
  children,
}: TextMenuWrapperProps) => (
  <BubbleMenu
    // Distinct from TableMenu: TipTap unregisterPlugin prefix-matches the key.
    pluginKey="textMenu"
    shouldShow={({ view, state, from }) =>
      shouldShow({ view, state, from }) && suppressed !== true
    }
    editor={editor}
    appendTo={() => getDialogContainer(editor)}
    getReferencedVirtualElement={() => getSelectionStartAnchor(editor)}
    options={{
      placement: 'top-start',
    }}
  >
    <Toolbar.Wrapper
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      {children}
    </Toolbar.Wrapper>
  </BubbleMenu>
);
